import React, { createContext, useCallback, useContext } from "react";
import { toast } from "sonner";

type ToastType = "success" | "error" | "info" | "warning" | "destructive";
type ToastPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

export interface Toast {
  id: number | string;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  variant: ToastType;
  autoDismiss?: boolean;
  position?: ToastPosition;
  timeout?: number;
}

interface ToastContextProps {
  showToast: (toast: Omit<Toast, "id"> & { id?: number | string }) => void;
  dismissToast?: (id: number | string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToast = (): ToastContextProps => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dismissToast = useCallback((id: number | string) => {
    toast.dismiss(id);
  }, []);

  const showToast = useCallback(
    (t: Omit<Toast, "id"> & { id?: number | string }) => {
      const options: Record<string, unknown> = {
        id: t.id,
        description: t.description,
        duration: t.autoDismiss === false ? Infinity : (t.timeout ?? 5000),
        action: t.actions
          ? { label: t.actions, onClick: () => toast.dismiss(t.id) }
          : undefined,
      };

      const variant = t.variant === "destructive" ? "error" : t.variant;

      switch (variant) {
        case "success":
          toast.success(t.title, options);
          break;
        case "error":
          toast.error(t.title, options);
          break;
        case "info":
          toast.info(t.title, options);
          break;
        case "warning":
          toast.warning(t.title, options);
          break;
        default:
          toast(t.title, options);
          break;
      }
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
};
